/** @typedef {import('./types').CrmImportRow} CrmImportRow */

export const MAX_IMPORT_ROWS = 2000

/** The columns the import API accepts. */
export const IMPORT_FIELDS = [
    { key: 'Name', label: 'Name', required: true },
    { key: 'Email', label: 'Email', required: false },
    { key: 'Company', label: 'Company', required: false },
    { key: 'Title', label: 'Job title', required: false },
    { key: 'Phone', label: 'Phone', required: false },
    { key: 'Tags', label: 'Tags', required: false },
]

/** Header spellings (after normalising) each field is recognised by. */
const SYNONYMS = {
    Name: ['name', 'fullname', 'contactname', 'contact', 'customername', 'person'],
    Email: ['email', 'emailaddress', 'mail', 'emailaddr', 'workemail', 'contactemail'],
    Company: ['company', 'companyname', 'account', 'accountname', 'organization', 'organisation', 'org', 'business', 'employer'],
    Title: ['title', 'jobtitle', 'position', 'role', 'jobrole', 'designation'],
    Phone: ['phone', 'phonenumber', 'telephone', 'tel', 'mobile', 'mobilephone', 'cell', 'cellphone', 'contactnumber'],
    Tags: ['tags', 'tag', 'labels', 'label', 'categories', 'category'],
}
const FIRST = ['firstname', 'first', 'givenname', 'fname', 'forename']
const LAST = ['lastname', 'last', 'surname', 'familyname', 'lname']

/** "E-mail Address " -> "emailaddress": case, spaces and punctuation do not matter. */
export const normalizeHeader = header =>
    String(header ?? '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '')

/**
 * Guess which CSV column feeds which field, by header name. Every column is used at most once; -1 means
 * "not found". When there is no Name column but separate first/last name columns, those are mapped too
 * and joined into the Name.
 * @param {string[]} headers
 * @returns {{ Name: number, Email: number, Company: number, Title: number, Phone: number, Tags: number, FirstName: number, LastName: number }}
 */
export const autoMapColumns = headers => {
    const normalized = (headers || []).map(normalizeHeader)
    const used = new Set()
    const find = names => {
        const index = normalized.findIndex((h, i) => !used.has(i) && h !== '' && names.includes(h))
        if (index >= 0) used.add(index)
        return index
    }
    const mapping = { Name: -1, Email: -1, Company: -1, Title: -1, Phone: -1, Tags: -1, FirstName: -1, LastName: -1 }
    IMPORT_FIELDS.forEach(field => {
        mapping[field.key] = find(SYNONYMS[field.key])
    })
    if (mapping.Name === -1) {
        mapping.FirstName = find(FIRST)
        mapping.LastName = find(LAST)
    }
    return mapping
}

/** The mapping is usable when a Name (or first/last name) column is chosen. */
export const hasNameColumn = mapping => mapping.Name >= 0 || mapping.FirstName >= 0 || mapping.LastName >= 0

const cell = (row, index) => (index >= 0 && index < row.length ? String(row[index]).trim() : '')

/**
 * Turn the CSV data rows (header removed) into API rows. Empty values are left out and rows with no
 * values at all are dropped.
 * @param {string[][]} dataRows
 * @param {ReturnType<typeof autoMapColumns>} mapping
 * @returns {CrmImportRow[]}
 */
export const buildImportRows = (dataRows, mapping) => {
    const out = []
    ;(dataRows || []).forEach(row => {
        const name = cell(row, mapping.Name) || [cell(row, mapping.FirstName), cell(row, mapping.LastName)].filter(Boolean).join(' ')
        /** @type {CrmImportRow} */
        const built = {}
        if (name) built.Name = name
        ;['Email', 'Company', 'Title', 'Phone', 'Tags'].forEach(key => {
            const value = cell(row, mapping[key])
            if (value) built[key] = value
        })
        if (Object.keys(built).length > 0) out.push(built)
    })
    return out
}

/** A user-facing message when there are too many rows, else null. */
export const importSizeError = count => {
    if (count > MAX_IMPORT_ROWS) {
        return `This file has ${count.toLocaleString('en-US')} rows. The most you can import at once is ${MAX_IMPORT_ROWS.toLocaleString('en-US')}. Split the file and import it in parts.`
    }
    return count === 0 ? 'No data rows were found in this file.' : null
}
